import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Input, Form } from 'antd';

import { getAnalyticsBusinessInfo, getMainAnalytics } from 'services/business';
import { nonSpecialCHRule } from 'utils/forms';

import OverallAnalytics from './OverallAnalytics/OverallAnalytics';
import DetailedAnalytics from './DetailedAnalytics/DetailedAnalytics';
import { notify } from 'components/Notify/Notify';
import LoadingWrapper from 'components/LoadingWrapper/LoadingWrapper';
import BusinessSelector from 'components/BRSelectors/BusinessSelector/BusinessSelector';

import './BusinessesAnalytics.less';

const BusinessesAnalytics = () => {
  const intl = useIntl();
  const searchRef = useRef();
  const [form] = Form.useForm();
  const [mainAnalytics, setMainAnalytics] = useState(null);
  const [businessData, setBusinessData] = useState({});
  const [activateEnterButton, setActivateEnterButton] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchMainAnalytics = async () => {
      setIsLoading(true);
      try {
        const {
          data: {
            bostaCredits,
            businessesInsuranceTypesCount,
            nextDayCashoutBusinessesCount,
            numberOfActiveBusinesses
          }
        } = await getMainAnalytics();

        setMainAnalytics({
          bostaCredits,
          pieData: businessesInsuranceTypesCount,
          cashOut: nextDayCashoutBusinessesCount,
          businessCount: numberOfActiveBusinesses
        });
      } catch (error) {
        notify(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMainAnalytics();
  }, []);

  const focusSearch = () => {
    const currentValue = searchRef.current?.input?.value;
    if (!currentValue) {
      searchRef.current?.focus();
    }
  };
  useEffect(() => {
    focusSearch();
  }, []);

  const handleOnClear = () => {
    setBusinessData(null);
    form.setFieldsValue({
      name: null,
      businessId: null
    });
    focusSearch();
  };

  const handleOnSearchWithBusinessName = async (businessId) => {
    if (!businessId) {
      handleOnClear();
      return;
    }

    setBusinessData(null);

    form.setFieldsValue({
      businessId: businessId?.value
    });

    setIsLoading(true);
    try {
      const { data } = await getAnalyticsBusinessInfo(
        businessId.value || businessId
      );
      setBusinessData(data);
      searchRef.current?.blur?.();
    } catch (error) {
      notify(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnSearchWithBusinessId = async (businessId) => {
    if (!businessId) {
      handleOnClear();
      return;
    }

    setBusinessData(null);

    form.setFieldsValue({
      name: null
    });

    setIsLoading(true);
    try {
      const { data } = await getAnalyticsBusinessInfo(businessId);
      setBusinessData(data);
      searchRef.current?.blur?.();
    } catch (error) {
      notify(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="br-businesses-analytics">
      <div className="br-businesses-analytics__header">
        <span className="heading">
          {intl.formatMessage({ id: 'merchant_analysis.business_analytics' })}
        </span>

        <div className="br-businesses-analytics__header-search">
          <Form form={form}>
            <Form.Item
              name="name"
              label={intl.formatMessage({
                id: 'merchant_analysis.business_name'
              })}
            >
              <BusinessSelector
                className="br-businesses-analytics__header-search-selector"
                placeholder={intl.formatMessage({
                  id: 'merchant_analysis.business_name'
                })}
                labelInValue
                onChange={handleOnSearchWithBusinessName}
                allowClear
                onClear={handleOnClear}
              />
            </Form.Item>
            <Form.Item
              name="businessId"
              label={intl.formatMessage({
                id: 'merchant_analysis.business_id'
              })}
              rules={[
                nonSpecialCHRule({
                  message: intl.formatMessage({
                    id: 'wallet.no_special_ch'
                  })
                })
              ]}
            >
              <Input.Search
                ref={searchRef}
                placeholder={intl.formatMessage({
                  id: 'merchant_analysis.business_id'
                })}
                allowClear
                onSearch={handleOnSearchWithBusinessId}
                enterButton={activateEnterButton}
                className="br-businesses-analytics__header-search-input"
                onChange={(e) => setActivateEnterButton(!!e.target.value)}
              />
            </Form.Item>
          </Form>
        </div>
      </div>
      <LoadingWrapper loading={isLoading}>
        <div className="br-businesses-analytics__info">
          {businessData?._id ? (
            <DetailedAnalytics
              businessData={businessData}
              setIsLoading={setIsLoading}
              isLoading={isLoading}
            />
          ) : (
            <OverallAnalytics
              analytics={mainAnalytics}
              setIsLoading={setIsLoading}
            />
          )}
        </div>
      </LoadingWrapper>
    </div>
  );
};

export default BusinessesAnalytics;
